/*
  assets/littleg-family-continuity.js — LITTLEG-FAMILY-CONTINUITY-05
  Owner: existing public, non-PII route context between Mission Share,
  Family Growth, Dashboard and GCityPartners.

  Exact consumers:
  - snippets/littleg-parent-continuation.liquid
  - sections/littleg-family-growth-hub.liquid

  Boundary:
  - Reads declared DOM attributes and URL parameters only.
  - No storage, user identity, photo/answer payload, history mutation,
    checkout, GFun mutation or API request.
*/
(function () {
  'use strict';

  var VERSION = 'littleg-family-continuity-05';
  var GLOBAL_KEY = '__LITTLEG_FAMILY_CONTINUITY_RUNTIME__';
  var PARENT_SELECTOR =
    '[data-parent-continuation][data-component-contract="LITTLE-G-PARENT-CONTINUATION-01"]';
  var GROWTH_SELECTOR =
    '[data-littleg-family-growth][data-section-contract="LITTLE-G-FAMILY-GROWTH-HUB-04"]';
  var originalHref = new WeakMap();
  /** @type {Set<DirectoryRoot>} */
  const mountedRoots = new Set();

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].init === 'function') {
    window[GLOBAL_KEY].init(document);
    return;
  }

  function clean(value, maxLength) {
    var output = String(value == null ? '' : value).trim();
    return maxLength ? output.slice(0, maxLength) : output;
  }

  function list(root, selector) {
    return root && root.querySelectorAll
      ? Array.prototype.slice.call(root.querySelectorAll(selector))
      : [];
  }

  function contextFromGame(root) {
    return {
      mission: clean(
        root &&
          (root.getAttribute('data-mission-handle') || root.getAttribute('data-mission-round-key')),
        120
      ),
      district: clean(root && root.getAttribute('data-district'), 120),
      city: clean(root && root.getAttribute('data-city'), 120) || 'hong-kong',
      cityPackId: clean(root && root.getAttribute('data-city-pack-id'), 80) || 'hk',
      serviceLane: clean(root && root.getAttribute('data-service-lane'), 120),
    };
  }

  function contextFromUrl() {
    var params;
    try {
      params = new URLSearchParams(window.location.search || '');
    } catch (_error) {
      params = new URLSearchParams();
    }

    return {
      mission: clean(params.get('mission'), 120),
      district: clean(params.get('district'), 120),
      city: clean(params.get('city'), 120) || 'hong-kong',
      cityPackId: clean(params.get('city_pack_id'), 80) || 'hk',
      serviceLane: clean(params.get('service_lane') || params.get('scenario'), 120),
    };
  }

  function sameOriginUrl(path) {
    var raw = clean(path);
    if (!raw || raw === '#' || raw.charAt(0) === '#') return null;

    try {
      var url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
      return url;
    } catch (_error) {
      return null;
    }
  }

  function withQuery(path, values, hash) {
    var url = sameOriginUrl(path);
    if (!url) return '';

    Object.keys(values || {}).forEach(function (key) {
      var value = clean(values[key], 160);
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    });

    if (hash) url.hash = hash;
    return url.pathname + url.search + url.hash;
  }

  function baseHref(link) {
    if (!link) return '';
    if (!originalHref.has(link)) {
      originalHref.set(link, clean(link.getAttribute('href')));
    }
    return originalHref.get(link) || '';
  }

  function patchLinks(scope, selector, values, hash) {
    list(scope, selector).forEach(function (link) {
      var href = withQuery(baseHref(link), values, hash);
      if (!href) {
        link.removeAttribute('data-route-context-ready');
        return;
      }
      link.setAttribute('href', href);
      link.setAttribute('data-route-context-ready', 'true');
    });
  }

  function mountParent(root) {
    if (!(root instanceof HTMLElement)) return;
    mountedRoots.add(root);

    var gameRoot = root.closest ? root.closest('[data-littleg-game-app]') : null;
    if (!gameRoot) gameRoot = document.querySelector('[data-littleg-game-app]');
    if (!gameRoot) {
      root.setAttribute('data-route-context-state', 'source-unavailable');
      return;
    }

    var context = contextFromGame(gameRoot);
    var shared = {
      mission: context.mission,
      district: context.district,
      city: context.city,
      city_pack_id: context.cityPackId,
      service_lane: context.serviceLane,
    };

    patchLinks(
      root,
      '[data-littleg-growth-route]',
      Object.assign(
        {
          view: 'growth',
          source: 'mission-share',
          intent: 'family-growth',
        },
        shared
      )
    );

    patchLinks(root, '[data-littleg-dashboard-route]', {
      view: 'littleg-dashboard',
      tab: 'home',
      source: 'mission-share',
      intent: 'family-return',
    });

    patchLinks(
      root,
      '[data-littleg-professional-route]',
      Object.assign(
        {
          source: 'littleg-result',
          intent: 'review-brief',
          scenario: context.serviceLane,
        },
        shared
      ),
      '#gcp-brief-form'
    );

    root.setAttribute('data-route-context-state', 'ready');
    root.setAttribute('data-family-continuity-version', VERSION);
  }

  function mountGrowth(root) {
    if (!(root instanceof HTMLElement)) return;
    mountedRoots.add(root);

    var context = contextFromUrl();
    root.setAttribute('data-context-mission', context.mission);
    root.setAttribute('data-context-district', context.district);
    root.setAttribute('data-context-service-lane', context.serviceLane);

    var contextNode = root.querySelector('[data-family-growth-context]');
    if (contextNode) {
      var parts = [];
      if (context.district) parts.push(context.district);
      if (context.mission) parts.push(context.mission.replace(/-/g, ' '));
      contextNode.textContent = parts.length
        ? '已帶入今次探索：' + parts.join(' · ')
        : '完成城市任務後，孩子的記憶卡和成長成果會在這裡延續。';
    }

    patchLinks(root, '[data-growth-dashboard-route]', {
      view: 'littleg-dashboard',
      tab: 'home',
      source: 'family-growth',
      intent: 'family-return',
    });

    patchLinks(root, '[data-growth-mission-route]', {
      view: 'game',
      screen: 'lobby',
      source: 'family-growth',
      intent: 'next-mission',
      district: context.district,
      city: context.city,
      city_pack_id: context.cityPackId,
    });

    root.setAttribute('data-route-context-state', 'ready');
    root.setAttribute('data-family-continuity-version', VERSION);
  }

  function mount(scope) {
    var target = scope || document;
    if (target.matches && target.matches(PARENT_SELECTOR)) mountParent(target);
    if (target.matches && target.matches(GROWTH_SELECTOR)) mountGrowth(target);
    list(target, PARENT_SELECTOR).forEach(mountParent);
    list(target, GROWTH_SELECTOR).forEach(mountGrowth);
  }

  function unmountRoot(root) {
    if (!root || !mountedRoots.has(root)) return;
    mountedRoots.delete(root);
    root.removeAttribute('data-route-context-state');
    root.removeAttribute('data-family-continuity-version');
    root.removeAttribute('data-context-mission');
    root.removeAttribute('data-context-district');
    root.removeAttribute('data-context-service-lane');
  }

  function unmount(scope) {
    var target = scope || document;
    if (target.matches && (target.matches(PARENT_SELECTOR) || target.matches(GROWTH_SELECTOR))) {
      unmountRoot(target);
    }
    list(target, PARENT_SELECTOR + ',' + GROWTH_SELECTOR).forEach(unmountRoot);

    if (!mountedRoots.size) {
      document.documentElement.removeAttribute('data-littleg-family-continuity');
    }
  }

  function init(scope) {
    mount(scope || document);
    if (mountedRoots.size) {
      document.documentElement.setAttribute('data-littleg-family-continuity', VERSION);
    }
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    unmount(event.target || document);
  });
  window.addEventListener('pageshow', function () {
    init(document);
  });
  window.addEventListener('popstate', function () {
    init(document);
  });

  window.LittleGFamilyContinuity = {
    version: VERSION,
    init: init,
    destroy: unmount,
  };
  window[GLOBAL_KEY] = window.LittleGFamilyContinuity;

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        init(document);
      },
      { once: true }
    );
  } else {
    init(document);
  }
})();
